/**
 * =========================================================================================
 *   Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *   Module       : components/errors/RemediationPanel
 *   Feature      : 252 - Backup Error Visibility & Guided Remediation
 *   Description  : The "what do I actually do about it" half of the error detail (FR-005).
 *                  Ordered steps, each markable individually (FR-011 — per STEP, not per error),
 *                  so the trail is precise enough to demonstrate the client exhausted the
 *                  self-service path before escalating (FR-012 / SC-005).
 *
 *                  🛑 FR-013: an ACCEPTED LIMITATION renders as an explained boundary, not an
 *                  actionable fix. Showing "mark attempted" on something that can never be fixed
 *                  would send an operator chasing it forever.
 *   Author       : Peter Dobler (Dobler Data Solutions)
 * =========================================================================================
 */
import React, { useState } from 'react';
import { CheckCircle2, CircleDashed, XCircle, MinusCircle, ExternalLink, Info } from 'lucide-react';
import { Badge, Button } from '@/components/ui';
import type { ResolvedRemediation, RemediationStep } from '../../api/backupErrors';

interface Props {
  remediation: ResolvedRemediation;
  isAcceptedLimitation: boolean;
  /** Undefined when the current role may not record attempts (FR-017). */
  onRecord?: (step: RemediationStep, outcome: 'attempted' | 'succeeded' | 'failed' | 'waived', note?: string) => Promise<void>;
  busy?: boolean;
}

const OUTCOME_ICON: Record<string, React.ReactNode> = {
  succeeded: <CheckCircle2 className="h-4 w-4 text-[var(--color-success)]" aria-hidden />,
  attempted: <CircleDashed className="h-4 w-4 text-[var(--color-text-secondary)]" aria-hidden />,
  failed: <XCircle className="h-4 w-4 text-[var(--color-danger)]" aria-hidden />,
  waived: <MinusCircle className="h-4 w-4 text-[var(--color-text-secondary)]" aria-hidden />,
};

export const RemediationPanel: React.FC<Props> = ({ remediation, isAcceptedLimitation, onRecord, busy }) => {
  const [openNote, setOpenNote] = useState<number | null>(null);
  const [note, setNote] = useState('');

  const record = async (step: RemediationStep, outcome: 'attempted' | 'succeeded' | 'failed' | 'waived') => {
    if (!onRecord) return;
    await onRecord(step, outcome, note || undefined);
    setOpenNote(null);
    setNote('');
  };

  return (
    <section className="mt-6" aria-label="Remediation">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {isAcceptedLimitation ? 'Why this cannot be fixed' : 'What to do about it'}
        </h3>
        {remediation.source === 'synthesized' && (
          <Badge variant="default" title="This code is not yet catalogued — follow the diagnostic to get it classified.">
            Diagnostic
          </Badge>
        )}
        {remediation.source === 'readiness_reason_codes' && (
          <Badge variant="info" title="Resolved from the pre-backup readiness catalog (single-step guidance).">
            Readiness guidance
          </Badge>
        )}
      </div>

      {remediation.explanation && (
        <p className="mt-2 text-sm text-[var(--color-text-secondary)]">{remediation.explanation}</p>
      )}

      {isAcceptedLimitation && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-subtle)] p-3">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-text-secondary)]" aria-hidden />
          <p className="text-sm text-[var(--color-text-secondary)]">
            This is a documented platform limitation, not a backup failure. It is recorded so the gap is
            known — there is no action that will clear it on the current configuration.
          </p>
        </div>
      )}

      <ol className="mt-3 space-y-3">
        {remediation.steps.map((step) => {
          const last = step.lastAttempt;
          return (
            <li key={step.n} className="rounded-md border border-[var(--color-border)] p-3">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--color-bg-subtle)] text-xs font-medium text-[var(--color-text-secondary)]">
                  {step.n}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-[var(--color-text-primary)]">{step.text}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    {!step.clientActionable && (
                      <Badge variant="info" title="Requires an action on our side or an Azure-admin grant.">
                        Not self-service
                      </Badge>
                    )}
                    {last && (
                      <span className="inline-flex items-center gap-1 text-xs text-[var(--color-text-secondary)]">
                        {OUTCOME_ICON[last.outcome]}
                        {last.outcome} by {last.by ?? 'unknown'}
                        {last.note ? ` — “${last.note}”` : ''}
                      </span>
                    )}
                  </div>

                  {/* FR-013: never offer a "fix it" affordance on an accepted limitation */}
                  {!isAcceptedLimitation && onRecord && (
                    openNote === step.n ? (
                      <div className="mt-2 space-y-2">
                        <textarea
                          className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] p-2 text-sm text-[var(--color-text-primary)]"
                          rows={2}
                          placeholder="Optional note (what you changed, what you saw)…"
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                        />
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" disabled={busy} onClick={() => record(step, 'succeeded')}>Succeeded</Button>
                          <Button size="sm" variant="secondary" disabled={busy} onClick={() => record(step, 'attempted')}>Attempted</Button>
                          <Button size="sm" variant="secondary" disabled={busy} onClick={() => record(step, 'failed')}>Didn’t help</Button>
                          <Button size="sm" variant="ghost" disabled={busy} onClick={() => record(step, 'waived')}>Not applicable</Button>
                          <Button size="sm" variant="ghost" onClick={() => { setOpenNote(null); setNote(''); }}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <Button size="sm" variant="ghost" className="mt-2" onClick={() => setOpenNote(step.n)}>
                        {last ? 'Record again' : 'Record outcome'}
                      </Button>
                    )
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      {remediation.docsUrl && (
        <a
          href={remediation.docsUrl}
          className="mt-3 inline-flex items-center gap-1 text-sm text-[var(--color-accent)] hover:underline"
        >
          Runbook <ExternalLink className="h-3.5 w-3.5" aria-hidden />
        </a>
      )}
    </section>
  );
};

export default RemediationPanel;
